import { lazy, Suspense, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { ContentGroups, TranslationNamespaces } from '~/lib/constants';
import { Tagged } from '~/apps/smartsend/components/Tagged';
import { ContentContext } from '@parcel2go/npm-p2g-app';
import { RoutingConstants } from '~/apps/smartsend/lib/consts/RoutingConstants';
import { GenerateBaseAnalyticsPayload } from '~/lib/utils/analytics';
import { NavigationContext } from '~/components/nav/nav-context';
import useOnLoad from '~/routes/use-on-load';
import { P2GBox } from '@parcel2go/core-ui';
import useGoto from '~/hooks/use-goto';
import { SettingsCache } from '~/lib/consts/settings-cache';
import useSmartSendAnalytics from '~/hooks/use-smartsend-analytics';

// Auto Top-Up management tab in the PrePay area. Dark-released behind the
// 'AutoTopUp' tenant feature flag - the flag only flips per tenant at the
// payments engine cutover (see docs/AUTO_PREPAY_TOP.md).
const AutoTopUp = lazy(() => import('~/apps/auto-topup/auto-topup'));

export default function Component() {
  const { goTo } = useGoto();
  const { track } = useSmartSendAnalytics();
  const { dispatch } = useContext(ContentContext);
  const { dispatch: navigationDispatch } = useContext(NavigationContext);
  const { t } = useTranslation(TranslationNamespaces.PREPAY);

  useOnLoad(() => {
    // Dark release: the route is always registered, so direct navigation must
    // bounce when the tenant flag is off. Customers must not be able to write
    // settings before the engine cutover (see docs/AUTO_PREPAY_TOP.md).
    if (!SettingsCache.settings.Features.includes('AutoTopUp')) {
      goTo(RoutingConstants.PrePay);
      return;
    }

    const title = t('AUTO_TOPUP_PAGE_TITLE', { defaultValue: 'Auto Top-Up' });
    document.title = title;
    dispatch({ type: 'SET_CONTENT_GROUP', contentGroup: ContentGroups.PREPAY });
    navigationDispatch({
      type: 'INITIALISE_NAVIGATION',
      payload: { title: title, currentPageRoute: RoutingConstants.AutoTopUp }
    });
    track({
      type: 'page_view',
      payload: {
        ...GenerateBaseAnalyticsPayload([]),
        page: RoutingConstants.AutoTopUp
      }
    });
  });

  return (
    <Tagged tag="screen-auto-topup">
      <Suspense>
        <P2GBox>
          <AutoTopUp />
        </P2GBox>
      </Suspense>
    </Tagged>
  );
}

Component.displayName = 'Auto Top-Up';
